// ===========================================================================
// Live headcounts for the rooms the site keeps open, on the landing page.
//
// "14 people in the quiet room right now" is the strongest front-door line in
// the research (Study Together's "40,504 online"), so it is drawn from the
// list the socket pushes (ServerMsg.ROOMS_OPEN: on connect, and the moment a
// room fills or empties or the operator changes the front page), adult band
// only; the server never publishes teen counts. It used to ask GET
// /api/rooms/standing every two seconds. When nothing has arrived, or the
// feature is off, the block simply is not there: a line that says "0 people"
// or "could not load" is worse than no line. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';

const MODE_LABEL = { video: 'Video', audio: 'Audio', text: t('Text') };

/** "14 people in the quiet room right now", or null when there is nothing
 *  honest to say. The quiet room is found by its flag, not its name. */
export function quietRoomLine(rooms) {
  const quiet = (rooms || []).find((r) => r && r.quiet);
  if (!quiet) return null;
  const n = Number(quiet.members) || 0;
  if (n <= 0) return null; // "0 people" reads as broken, not as an invitation
  return `${n} ${n === 1 ? 'person' : 'people'} in ${quiet.name || 'the quiet room'} right now`;
}

/** The short count for one room row. Never "0 people". */
export function describeRoom(r) {
  const here = Number(r?.members) || 0;
  const watching = Number(r?.watching) || 0;
  if (here <= 0) return 'Quiet right now';
  const parts = [`${here} here`];
  if (watching > 0) parts.push(`${watching} watching`);
  return parts.join(', ');
}

/** Where a row goes when it is tapped, or null for a row that is only a
 *  count. Only a promoted room has somewhere to go: it is somebody's room
 *  rather than one of the site's, so its code is the whole point of listing
 *  it. A code that does not look like a code gets no link at all. */
/**
 * The heading for a list of rooms, given what is in it.
 *
 * "Rooms that are always open" is true only while Lumi's own rooms are in the
 * list. The operator can take them off the front page (the frontPageRooms
 * switch) without closing them, and then every row is somebody's promoted
 * room -- a different thing, which needs a different word above it.
 *
 * @param {Array<{ promoted?: boolean }>} rooms
 */
export function roomsHeading(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const anyStanding = list.some((r) => !r.promoted);
  // Both spelled out, because the string scanner reads t('...') literals to
  // work out which catalog keys are real.
  return anyStanding ? t('Rooms that are always open') : t('Rooms people are hosting');
}

export function roomHref(r) {
  const code = typeof r?.code === 'string' ? r.code.toUpperCase() : '';
  if (!r?.promoted || !/^[A-Z0-9]{3,12}$/.test(code)) return null;
  return `/r/${code}`;
}

/** A language tag for the row, or null. Short and honest: the tag, upper case. */
export function langTag(lang) {
  return typeof lang === 'string' && lang ? lang.toUpperCase().slice(0, 8) : null;
}

/**
 * Fill `host` with the quiet-room line and the compact list, from what the
 * socket pushes. Nothing is drawn until the first list arrives, so a slow
 * socket shows no line rather than a wrong one, and a list of nothing takes
 * the block away.
 * @returns {{ apply: (body: { rooms?: unknown }) => void }}
 */
export function mountRoomCounts(host) {
  function draw(rooms) {
    if (!rooms || !rooms.length) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }
    const line = quietRoomLine(rooms);
    // filter(Boolean), because replaceChildren does NOT skip a null the way
    // el()'s children do: it stringifies it. With no quiet-room line -- which
    // is most of the time, since an empty quiet room says nothing -- the word
    // "null" was printed above the heading.
    const children = [
      line ? el('p', { class: 'open-rooms-line', text: line }) : null,
      el('p', { class: 'open-rooms-title', text: roomsHeading(rooms) }),
      el('ul', { class: 'open-rooms-list' }, rooms.map((r) => {
        const href = roomHref(r);
        const inner = [
          el('span', { class: 'open-rooms-name', text: r.name || r.slug }),
          // Said out loud, on every promoted row. A paid placement that looks
          // like the site's own recommendation is the one thing this feature
          // must not become, and the word costs a row nothing.
          r.promoted ? el('span', { class: 'room-promoted', text: t('Promoted') }) : null,
          // A promoted teen room, on a page everybody reads: said on the row,
          // so an adult knows before tapping that it is not for them (the
          // operator, 2026-09-16). It carries no headcount: the server sends
          // none for a teen room, and the row does not pretend to one.
          r.band === 'minor' ? el('span', { class: 'room-teens', text: t('Teens only') }) : null,
          el('span', { class: `room-mode mode-${r.mode || 'video'}`, text: MODE_LABEL[r.mode] || 'Video' }),
          langTag(r.lang) ? el('span', { class: 'room-lang', text: langTag(r.lang) }) : null,
          r.band === 'minor' ? null : el('span', { class: 'open-rooms-count', text: describeRoom(r) }),
        ].filter(Boolean);
        // A standing room is a count and nothing more: it has its own card in
        // the hub, and a second way in from here would be a second thing to
        // keep right. A promoted room has no card anywhere, so the row IS the
        // way in.
        return el('li', { class: r.promoted ? 'open-rooms-row is-promoted' : 'open-rooms-row' }, [
          href ? el('a', { class: 'open-rooms-link', href }, inner) : el('span', { class: 'open-rooms-inner' }, inner),
        ]);
      })),
    ];
    host.replaceChildren(...children.filter(Boolean));
    host.hidden = false;
    // This block changes on its own, so it says so. It used to be covered by
    // an aria-live on the whole application, which announced every screen
    // change on the site as an update and so announced nothing usefully.
    // Optional, because the drawing is unit-tested against a plain object
    // standing in for the element, and an announcement is not what those
    // tests are about.
    host.setAttribute?.('role', 'status');
    host.setAttribute?.('aria-live', 'polite');
  }

  host.hidden = true; // nothing until the first list arrives
  return {
    /** A list pushed down the socket. Same shape the route returns. */
    apply(body) {
      const rows = Array.isArray(body?.rooms) ? body.rooms : null;
      if (!rows) return;
      draw(rows);
    },
  };
}

export default { mountRoomCounts, quietRoomLine, describeRoom, langTag, roomHref };
